import { t, ui, uiError } from '../i18n';
import { validationFieldMessages } from '../i18n/validation';
import { getWebappApiUrl, webappFetch } from '../utils/apiConfig';
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  PersonAdd as PersonAddIcon,
  Group as GroupIcon,
  Check as CheckIcon,
  Close as CloseIcon
} from '@mui/icons-material';

// 🆕 추가할 import
import {
  Switch,
  FormControlLabel
} from '@mui/material';
import { mapOrganizationName, mapNodeId } from '../utils/organizationMapping';

const NodeManagement = () => {
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [statusCounts, setStatusCounts] = useState({
    total: 0,
    activated: 0, // Will be loaded from API
    approved: 0,
    pending: 0,
    rejected: 0
  });

  // 🆕 Filter selection state
  const [selectedFilter, setSelectedFilter] = useState('all');
  

  // 🆕 v5.0 확장 필드
  const [newNode, setNewNode] = useState({
    nodeId: '',
    nodeName: '',
    orgType: '',
    orgTypeCustom: '',
    adminName: '',
    companyEmail: '',
    companyPhone: '',
    location: '',
    locationCustom: '',
    endpoint: ''
  });
  const [validationErrors, setValidationErrors] = useState({});

  // 🆕 부산신항만 맞춤 조직 유형 옵션
  const orgTypeOptions = [
    { value: '항만운영업체', label: '항만운영업체' },
    { value: '컨테이너터미널', label: '컨테이너터미널' },
    { value: '선박회사', label: '선박회사' },
    { value: '화물운송업체', label: '화물운송업체' },
    { value: '물류창고업체', label: '물류창고업체' },
    { value: '세관브로커', label: '세관브로커' },
    { value: '포워딩업체', label: '포워딩업체' },
    { value: '기타', label: '기타 (직접입력)' }
  ];

  // 🆕 필터링 관련 상태
  const [filteredNodes, setFilteredNodes] = useState([]);
  const [nodeFilters, setNodeFilters] = useState({
    status: '',
    orgType: ''
  });
  
  // 🆕 거부된 노드 숨김 상태 (localStorage와 연동)
  const [hideRejected, setHideRejected] = useState(
    localStorage.getItem('hideRejectedNodes') === 'true'
  );

  // 🆕 실제 활성화된 노드 데이터 (API에서 로드됨)
  const [activatedNodesData, setActivatedNodesData] = useState([]);

  // 🆕 필터 옵션 정의 (실제 데이터에서 추출된 값들)
  const nodeFilterOptions = {
    status: ['Approved', 'Pending', 'Rejected'],
    orgType: ['세관브로커', '항만운영업체', '컨테이너터미널', 'BusanPort', 'ContainerOperator', 'PortAuthority', 'TestOrg']
  };

  // 🆕 부산신항만 맞춤 위치 옵션
  const locationOptions = [
    { value: '부산신항만 북컨테이너터미널', label: '부산신항만 북컨테이너터미널' },
    { value: '부산신항만 서컨테이너터미널', label: '부산신항만 서컨테이너터미널' },
    { value: '부산신항만 제2터미널', label: '부산신항만 제2터미널' },
    { value: '부산신항만 동측부두', label: '부산신항만 동측부두' },
    { value: '부산신항만 서측부두', label: '부산신항만 서측부두' },
    { value: '부산신항만 배후단지', label: '부산신항만 배후단지' },
    { value: '기타', label: '기타 위치 (직접입력)' }
  ];

  // 🆕 Node Management 데이터 조회 (새로운 API 사용)
  const fetchNodeManagement = async () => {
    try {
      setLoading(true);
      
      const response = await webappFetch(`${getWebappApiUrl()}/api/blockchain-infra/node-management`);
      
      const result = await response.json();
      
      if (result.success) {
        const data = result.data;
        
        // 상태별 카운트 업데이트
        setStatusCounts({
          total: data.registeredNodes.count,
          activated: data.activatedNodes.count,
          approved: data.approvedNodes.count,
          pending: data.pendingApproval.count,
          rejected: data.rejectedNodes.count
        });
        
        // 활성화된 노드 데이터 저장
        setActivatedNodesData(data.activatedNodes.nodes);
        
        // 등록된 노드들을 nodes 상태에 설정 (UI 테이블 표시용으로 변환)
        const registeredNodesForTable = data.registeredNodes.nodes.map(node => {
          return {
            id: node.id,
            name: node.name,
            orgType: mapOrganizationName(node.organization),
            adminName: 'System Administrator',
            location: `${node.location} (${node.ip})`,
            status: 'Active',
            isPhysical: true,
            nodeRole: node.role,
            endpoint: node.ports && node.ports.length > 0 ? `grpcs://${node.ip}:${node.ports[0].split(':')[0]}` : `grpcs://${node.ip}:7051`
          };
        });
        
        
        setNodes(registeredNodesForTable);
        
        
        setError('');
      } else {
        setError(result.message || 'Node Management 정보를 가져오는데 실패했습니다');
      }
    } catch (error) {
      setError('서버 연결에 실패했습니다: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // 노드 목록 조회 (기존 membership API - 현재는 빈 상태)
  const fetchNodes = async () => {
    try {
      const response = await webappFetch(`${getWebappApiUrl()}/api/membership/nodes`);
      const result = await response.json();
      
      
      if (result.success && result.data && result.data.length > 0) {
        setNodes(result.data);
      } else {
        // nodes 상태를 변경하지 않음 (fetchNodeManagement에서 설정한 값 유지)
      }
    } catch (error) {
      // 오류 시에도 nodes 상태를 변경하지 않음
    }
  };

  useEffect(() => {
    // fetchNodeManagement를 먼저 실행하고, 완료 후 fetchNodes 실행
    const loadData = async () => {
      await fetchNodeManagement(); // 새로운 Node Management API (우선순위)
      await fetchNodes(); // 기존 membership API (보조)
    };
    loadData();
  }, []);

  // 데이터 로드 완료 후 기본 필터 적용 (초기 로드 시에만)
  useEffect(() => {
    if (nodes.length > 0 && filteredNodes.length === 0 && selectedFilter === 'all') {
      applyStatusFilter('all'); // 기본적으로 모든 등록된 노드 표시
    }
  }, [nodes]); // selectedFilter와 filteredNodes 의존성 제거하여 무한 루프 방지

  // 🆕 nodes가 변경될 때마다 filteredNodes 업데이트
  useEffect(() => {
    let filtered = [...nodes];
    
    // 거부된 노드 숨김 처리
    if (hideRejected) {
      filtered = filtered.filter(node => 
        node.status !== 'Rejected' && node.status !== 'rejected'
      );
    }
    
    setFilteredNodes(filtered);
  }, [nodes, hideRejected]);

  // 🆕 필터링 로직 함수들
  const applyNodeFilters = () => {
    let filtered = [...nodes];
    
    // 1. 거부된 노드 숨김 처리 (최우선)
    if (hideRejected) {
      filtered = filtered.filter(node => 
        node.status !== 'Rejected' && node.status !== 'rejected'
      );
    }
    
    // 2. 상태 필터 적용
    if (nodeFilters.status && nodeFilters.status !== '') {
      filtered = filtered.filter(node => node.status === nodeFilters.status);
    }
    
    // 3. 조직 유형 필터 적용
    if (nodeFilters.orgType && nodeFilters.orgType !== '') {
      filtered = filtered.filter(node => node.orgType === nodeFilters.orgType);
    }
    
    setFilteredNodes(filtered);
  };

  // 🆕 필터 변경 핸들러
  const handleNodeFilterChange = (field, value) => {
    setNodeFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // 🆕 필터 초기화 함수
  const resetNodeFilters = () => {
    setNodeFilters({
      status: '',
      orgType: ''
    });
    setFilteredNodes(nodes);
  };

  // 🆕 Status Filter Handler for Stat Cards
  const applyStatusFilter = (filterType) => {
    
    setSelectedFilter(filterType);
    let filtered = [];
    
    switch(filterType) {
      case 'all':
        // Show registered nodes with 'Registered' status
        filtered = [...nodes].map(node => ({
          ...node,
          status: 'Registered'
        }));
        break;
      case 'activated':
        // Show actual activated nodes from API with 'Activated' status
        filtered = activatedNodesData.map(node => ({
          id: mapNodeId(node.id),
          name: mapNodeId(node.name),
          orgType: mapOrganizationName(node.organizationName),
          adminName: 'System Administrator',
          location: `${node.location} (${node.ip})`,
          status: 'Activated',
          isPhysical: true,
          nodeRole: node.role,
          endpoint: `grpcs://${node.ip}:${node.ports[0].split(':')[0]}`
        }));
        break;
      case 'approved':
        filtered = [...nodes].filter(node => node.status === 'Approved');
        break;
      case 'pending':
        filtered = [...nodes].filter(node => node.status === 'Pending');
        break;
      case 'rejected':
        filtered = [...nodes].filter(node => node.status === 'Rejected');
        break;
      default:
        filtered = [...nodes];
        break;
    }
    
    // Apply hide rejected filter if needed
    if (hideRejected && filterType !== 'rejected') {
      filtered = filtered.filter(node => 
        node.status !== 'Rejected' && node.status !== 'rejected'
      );
    }
    
    setFilteredNodes(filtered);
  };

  // 🆕 Card Click Handler
  // Get dynamic title based on selected filter
  const getListTitle = () => {
    switch (selectedFilter) {
      case 'all': return 'Registered Node List';
      case 'activated': return 'Activated Node List';
      case 'approved': return 'Approved Node List';
      case 'pending': return 'Pending Node List';
      case 'rejected': return 'Rejected Node List';
      default: return 'Node List';
    }
  };

  const handleCardClick = (filterType) => {
    applyStatusFilter(filterType);
  };

  // 🆕 거부된 노드 숨김 토글
  const toggleHideRejected = () => {
    const newHideState = !hideRejected;
    setHideRejected(newHideState);
    localStorage.setItem('hideRejectedNodes', newHideState.toString());
    
    // 필터 즉시 재적용 (setTimeout 제거)
    applyNodeFilters();
  };

  // 🆕 v5.0 입력 검증 함수들
  const validateNodeId = (nodeId) => {
    if (!nodeId) return '노드 ID는 필수입니다';
    if (!/^[a-zA-Z0-9_]{3,20}$/.test(nodeId)) {
      return '3-20자의 영문자, 숫자, 언더스코어만 허용됩니다';
    }
    return '';
  };

  const validateEmail = (email) => {
    if (!email) return '이메일은 필수입니다';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return '올바른 이메일 형식을 입력하세요';
    }
    return '';
  };

  const validatePhone = (phone) => {
    if (!phone) return '전화번호는 필수입니다';
    if (!/^(010|011|016|017|018|019|02|031|032|033|041|042|043|044|051|052|053|054|055|061|062|063|064)-\d{3,4}-\d{4}$/.test(phone)) {
      return '올바른 전화번호 형식을 입력하세요 (예: 010-1234-5678, 051-123-4567)';
    }
    return '';
  };

  const validateEndpoint = (endpoint) => {
    if (endpoint && !/^https?:\/\/.+:\d+$/.test(endpoint)) {
      return 'http://domain:port 형식으로 입력하세요 (예: http://example.com:7051)';
    }
    return '';
  };

  // 입력값 검증
  const validateForm = () => {
    const errors = {
      nodeId: validateNodeId(newNode.nodeId),
      nodeName: !newNode.nodeName ? '노드 이름은 필수입니다' : '',
      orgType: !newNode.orgType ? '조직 유형은 필수입니다' : '',
      orgTypeCustom: newNode.orgType === '기타' && !newNode.orgTypeCustom ? '기타 조직 유형을 입력하세요' : '',
      adminName: !newNode.adminName ? 'Administrator 이름은 필수입니다' : '',
      companyEmail: validateEmail(newNode.companyEmail),
      companyPhone: validatePhone(newNode.companyPhone),
      location: !newNode.location ? '위치는 필수입니다' : '',
      locationCustom: newNode.location === '기타' && !newNode.locationCustom ? '기타 위치를 입력하세요' : '',
      endpoint: validateEndpoint(newNode.endpoint)
    };

    setValidationErrors(errors);
    return !Object.values(errors).some(error => error !== '');
  };

  // 🆕 노드 등록 (v5.0 API)
  const handleRegisterNode = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitLoading(true);
      
      // 🆕 "기타" 선택 시 커스텀 값 사용
      const finalOrgType = newNode.orgType === '기타' ? newNode.orgTypeCustom : newNode.orgType;
      const finalLocation = newNode.location === '기타' ? newNode.locationCustom : newNode.location;
      
      const nodeData = {
        ...newNode,
        orgType: finalOrgType,
        location: finalLocation
      };
      
      const response = await webappFetch(`${getWebappApiUrl()}/api/membership/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(nodeData),
      });

      const result = await response.json();
      
      if (result.success) {
        setOpenDialog(false);
        setNewNode({
          nodeId: '',
          nodeName: '',
          orgType: '',
          orgTypeCustom: '',
          adminName: '',
          companyEmail: '',
          companyPhone: '',
          location: '',
          locationCustom: '',
          endpoint: ''
        });
        setValidationErrors({});
        await fetchNodes();
        await fetchNodeManagement(); // 새로운 API도 새로고침
        setError('');
      } else {
        if (result.validationErrors || result.validationDetails) {
          // 서버 검증 오류를 표시
          const serverErrors = validationFieldMessages(result);
          setValidationErrors(prev => ({ ...prev, ...serverErrors }));
          if (Object.keys(serverErrors).length === 0) {
            setError('The request failed. Please try again or contact the administrator.');
          }
        } else {
          setError(result.error || '노드 등록에 실패했습니다');
        }
      }
    } catch (error) {
      setError('서버 연결에 실패했습니다');
    } finally {
      setSubmitLoading(false);
    }
  };

  // 🆕 노드 승인
  const handleApproveNode = async (nodeId) => {
    try {
      const response = await webappFetch(`${getWebappApiUrl()}/api/membership/approve/${nodeId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ adminId: 'admin' })
      });

      const result = await response.json();
      
      if (result.success) {
        await fetchNodes();
        await fetchNodeManagement(); // 새로운 API도 새로고침
        setError('');
      } else {
        setError(result.error || '노드 승인에 실패했습니다');
      }
    } catch (error) {
      setError('서버 연결에 실패했습니다');
    }
  };

  // 🆕 노드 거부
  const handleRejectNode = async (nodeId) => {
    try {
      const response = await webappFetch(`${getWebappApiUrl()}/api/membership/reject/${nodeId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ adminId: 'admin' })
      });

      const result = await response.json();
      
      if (result.success) {
        await fetchNodes();
        await fetchNodeManagement(); // 새로운 API도 새로고침
        setError('');
      } else {
        setError(result.error || '노드 거부에 실패했습니다');
      }
    } catch (error) {
      setError('서버 연결에 실패했습니다');
    }
  };

  // 🆕 Status display with matching colors to stat cards
  const getStatusDisplay = (status) => {
    switch (status) {
      case 'Approved':
        return { text: 'Approved', color: 'success' };
      case 'Pending':
        return { text: 'Pending', color: 'info' };
      case 'Rejected':
        return { text: 'Rejected', color: 'error' };
      default:
        return { text: status, color: 'default' };
    }
  };

  return (
    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#1976d2', fontWeight: 'bold' }}>
          <GroupIcon sx={{ fontSize: 34 }} />{t("Node Management")}</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>{t("Review registered Fabric nodes, activation status, and organization registry information.")}</Typography>
      </Box>

      {/* 🆕 상태별 통계 카드 */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Registered Nodes */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Card 
            sx={{ 
              cursor: 'pointer',
              border: selectedFilter === 'all' ? '2px solid #1976d2' : '1px solid #e0e0e0',
              '&:hover': { boxShadow: 3 }
            }}
            onClick={() => handleCardClick('all')}
          >
            <CardContent>
              <Typography variant="h6" color="primary">{t("Registered Nodes")}</Typography>
              <Typography variant="h3">{statusCounts.total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Activated Nodes */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Card 
            sx={{ 
              cursor: 'pointer',
              border: selectedFilter === 'activated' ? '2px solid #1976d2' : '1px solid #e0e0e0',
              '&:hover': { boxShadow: 3 }
            }}
            onClick={() => handleCardClick('activated')}
          >
            <CardContent>
              <Typography variant="h6" color="warning.main">{t("Activated Nodes")}</Typography>
              <Typography variant="h3">{statusCounts.activated}</Typography>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Approved Nodes */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Card 
            sx={{ 
              cursor: 'pointer',
              border: selectedFilter === 'approved' ? '2px solid #1976d2' : '1px solid #e0e0e0',
              '&:hover': { boxShadow: 3 }
            }}
            onClick={() => handleCardClick('approved')}
          >
            <CardContent>
              <Typography variant="h6" color="success.main">{t("Approved Nodes")}</Typography>
              <Typography variant="h3">{statusCounts.approved}</Typography>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Pending Nodes */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Card 
            sx={{ 
              cursor: 'pointer',
              border: selectedFilter === 'pending' ? '2px solid #1976d2' : '1px solid #e0e0e0',
              '&:hover': { boxShadow: 3 }
            }}
            onClick={() => handleCardClick('pending')}
          >
            <CardContent>
              <Typography variant="h6" color="info.main">{t("Pending Approval")}</Typography>
              <Typography variant="h3">{statusCounts.pending}</Typography>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Rejected Nodes */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Card 
            sx={{ 
              cursor: 'pointer',
              border: selectedFilter === 'rejected' ? '2px solid #1976d2' : '1px solid #e0e0e0',
              '&:hover': { boxShadow: 3 }
            }}
            onClick={() => handleCardClick('rejected')}
          >
            <CardContent>
              <Typography variant="h6" color="error.main">{t("Rejected Nodes")}</Typography>
              <Typography variant="h3">{statusCounts.rejected}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 🆕 Node Search Filter */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>{t("Node Search Filter")}</Typography>
          
          <Grid container spacing={2} alignItems="center">
            {/* Status Filter */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>{t("Status")}</InputLabel>
                <Select
                  value={nodeFilters.status}
                  label={t("Status")}
                  onChange={(e) => handleNodeFilterChange('status', e.target.value)}
                >
                  <MenuItem value="">{t("All")}</MenuItem>
                  {nodeFilterOptions.status.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option === 'Approved' ? t("Approved") : option === 'Pending' ? t("Pending") : option === 'Rejected' ? t("Rejected") : ui(option)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Organization Type Filter */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>{t("Organization Type")}</InputLabel>
                <Select
                  value={nodeFilters.orgType}
                  label={t("Organization Type")}
                  onChange={(e) => handleNodeFilterChange('orgType', e.target.value)}
                >
                  <MenuItem value="">{t("All")}</MenuItem>
                  {nodeFilterOptions.orgType.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Search/Reset Buttons */}
            <Grid item xs={12} sm={6} md={6}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={applyNodeFilters}
                  size="small"
                >{t("Search")}</Button>
                <Button
                  variant="outlined"
                  onClick={resetNodeFilters}
                  size="small"
                >{t("Reset")}</Button>
              </Box>
            </Grid>

            {/* Hide Rejected Nodes Toggle */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={hideRejected}
                    onChange={toggleHideRejected}
                    color="warning"
                  />
                }
                label={t("Hide Rejected Nodes {0}", ['(' + t(hideRejected ? 'ON' : 'OFF') + ')'])}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" component="h2">
              {ui(getListTitle())}
            </Typography>
            {/* 노드 가입 요청 버튼 - 향후 사용을 위해 주석 처리
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => setOpenDialog(true)}
              disabled={loading}
            >
              Node Registration Request
            </Button>
            */}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {uiError(error)}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t("Node ID")}</TableCell>
                    <TableCell>{t("Node Name")}</TableCell>
                    <TableCell>{t("Organization Type")}</TableCell>
                    <TableCell>{t("Administrator")}</TableCell>
                    <TableCell>{t("Location")}</TableCell>
                    <TableCell>{t("Status")}</TableCell>
                    <TableCell>{t("Action")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredNodes.map((node) => {
                    const statusDisplay = getStatusDisplay(node.status);
                    return (
                      <TableRow key={node.id}>
                        <TableCell>{node.id}</TableCell>
                        <TableCell>{node.name}</TableCell>
                        <TableCell>{node.orgType || '-'}</TableCell>
                        <TableCell>{node.isPhysical && node.adminName === 'System Administrator' ? t('System Administrator') : node.adminName || '-'}</TableCell>
                        <TableCell>{node.location || '-'}</TableCell>
                        <TableCell>
                          {/* Show "Activated" chip only for physical blockchain nodes */}
                          {node.isPhysical ? (
                            <Chip 
                              label={t("Activated")}
                              color="warning" 
                              size="small" 
                            />
                          ) : (
                            <Chip
                              label={ui(statusDisplay.text)}
                              color={statusDisplay.color}
                              size="small"
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {node.status === 'Pending' && (
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Tooltip title={t("Approve")}>
                                <IconButton
                                  color="success"
                                  size="small"
                                  onClick={() => handleApproveNode(node.id)}
                                >
                                  <CheckIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={t("Reject")}>
                                <IconButton
                                  color="error"
                                  size="small"
                                  onClick={() => handleRejectNode(node.id)}
                                >
                                  <CloseIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                          {node.status !== 'Pending' && (
                            <Typography variant="body2" color="text.secondary">
                              -
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* 🆕 Node Registration Dialog - Commented out for future use
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>New Node Registration Request</DialogTitle>
        <DialogContent>
          ... (Dialog content removed for simplicity)
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleRegisterNode} variant="contained" disabled={submitLoading}>
            {submitLoading ? <CircularProgress size={20} /> : 'Submit Request'}
          </Button>
        </DialogActions>
      </Dialog>
      */}
    </Box>
  );
};

export default NodeManagement;
